import React, { useRef, useEffect, useState } from "react";
import { Chart, registerables } from "chart.js/auto";
import { Tag, Table, Form, Select, Button, Pagination } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { getMous } from "../features/mou/mouSlice";
import { getJobtrades, getSectors } from "../features/brand/brandSlice";
import { formatDate, getDateDifference } from "../utils/dateHelper";
import { CSVLink } from "react-csv";

Chart.register(...registerables);

const columns = (openPdf) => [
  {
    title: "S.NO.",
    dataIndex: "key",
    key: "key",
  },
  {
    title: "STATE",
    dataIndex: "state",
    key: "state",
  },
  {
    title: "SCHOOL COUNT",
    dataIndex: "schoolCount",
    width: 5,
    key: "schoolCount",
  },
  {
    title: "Trade",
    dataIndex: "trade_name",
    key: "trade_name",
  },
  {
    title: "Job Roles",
    dataIndex: "job_roles",
    key: "job_roles",
  },
  {
    title: "School Category",
    dataIndex: "School_category",
    key: "School_category",
  },
  {
    title: "DATE FROM",
    dataIndex: "dateFrom",
    key: "dateFrom",
  },
  {
    title: "DATE TO",
    dataIndex: "dateTo",
    key: "dateTo",
  },
  {
    title: "NO. DAYS LEFT",
    dataIndex: "daysLeft",
    key: "daysLeft",
  },
];

const MouStatus = () => {
  const [selectedState, setSelectedState] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [chartData, setChartData] = useState({
    active: 0,
    expired: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getJobtrades());
    dispatch(getSectors());
    dispatch(getStates());
    dispatch(getMous());
  }, [dispatch]);

  const moulistState = useSelector((state) => state.mou.mous);
  const statelistState = useSelector((state) => state.statecity.statelist);

  // Load all data on initial render
  useEffect(() => {
    if (moulistState && moulistState.length > 0) {
      const today = new Date().setHours(0, 0, 0, 0);
      let activeCount = 0;
      let expiredCount = 0;

      const data = moulistState.map((MOU, index) => {
        const MOUDate = new Date(MOU.to_date).setHours(0, 0, 0, 0);
        const Moudifference = getDateDifference(today, MOU.to_date);

        if (today <= MOUDate) {
          activeCount++;
        } else {
          expiredCount++;
        }

        return {
          key: index + 1,
          state: MOU.state,
          schoolCount: MOU.school_count,
          trade_name: MOU.trade_name,
          job_roles: MOU.job_roles,
          School_category: MOU.School_category,
          dateFrom: formatDate(MOU.from_date),
          dateTo: formatDate(MOU.to_date),
          daysLeft: (
            <Tag color={Moudifference.days < 90 ? "red" : "green"}>
              {today >= MOUDate ? "MOU Expired" : `${Moudifference.days} Days`}
            </Tag>
          ),
          pdfUrl: MOU.pdfUrl,
        };
      });

      setFilteredData(data);
      setChartData({
        active: activeCount,
        expired: expiredCount,
      });
    }
  }, [moulistState]);

  useEffect(() => {
    const ctx = chartRef.current.getContext("2d");
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    chartInstance.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels: ["Active MoUs", "Expired MoUs"],
        datasets: [
          {
            data: [chartData.active, chartData.expired],
            backgroundColor: ["#4CAF50", "#FFC107"],
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: "top",
          },
        },
      },
    });
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData]);

  const handleStateChange = (value) => {
    setSelectedState(value);
  };

  const openPdf = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  const handleSearch = (values) => {
    let filtered = moulistState;
    if (values.state) {
      filtered = filtered.filter((mou) => mou.state === selectedState);
    }

    const today = new Date().setHours(0, 0, 0, 0);
    let activeCount = 0;
    let expiredCount = 0;

    const data = filtered.map((MOU, index) => {
      const MOUDate = new Date(MOU.to_date).setHours(0, 0, 0, 0);
      const Moudifference = getDateDifference(today, MOU.to_date);

      if (today <= MOUDate) {
        activeCount++;
      } else {
        expiredCount++;
      }

      return {
        key: index + 1,
        state: MOU.state,
        schoolCount: MOU.school_count,
        trade_name: MOU.trade_name,
        job_roles: MOU.job_roles,
        School_category: MOU.School_category,
        dateFrom: formatDate(MOU.from_date),
        dateTo: formatDate(MOU.to_date),
        daysLeft: (
          <Tag color={Moudifference.days < 90 ? "red" : "green"}>
            {today >= MOUDate ? "MOU Expired" : `${Moudifference.days} Days`}
          </Tag>
        ),
        pdfUrl: MOU.pdfUrl,
      };
    });

    setFilteredData(data);
    setChartData({
      active: activeCount,
      expired: expiredCount,
    });
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Pagination logic
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const csvHeaders = [
    { label: "State", key: "state" },
    { label: "School Count", key: "schoolCount" },
    { label: "Trade", key: "trade_name" },
    { label: "Job Role", key: "job_roles" },
    { label: "School Category", key: "School_category" },
    { label: "Date From", key: "dateFrom" },
    { label: "Date To", key: "dateTo" },
    { label: "Days Left", key: "daysLeft" },
  ];

  return (
    <div className="bg-gray-100 p-6 min-h-screen">
      <h2
        style={{
          fontSize: "large",
          fontWeight: "bold",
          margin: "-5px 0 20px -400px",
        }}
      >
        MOU Expiry Tracker - Detailed Overview
      </h2>

      {/* Filters Section */}
      <Form layout="inline" onFinish={handleSearch}>
        <Form.Item
          name="state"
          rules={[{ required: true, message: "Please Select State!" }]}
        >
          <Select
            placeholder="Select State"
            onChange={handleStateChange}
            style={{ width: 120 }}
          >
            {statelistState.map((i) => (
              <Select.Option key={i.code} value={i.state_name}>
                {i.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            View
          </Button>
        </Form.Item>
      </Form>

      {/* Graph Section */}
      <div
        style={{
          marginTop: "20px",
          padding: "10px",
          width: "100%",
          height: "280px",
          backgroundColor: "#DCDCDC",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <canvas ref={chartRef} className="w-full h-full"></canvas>
      </div>

      {/* Table Section */}
      <div
        className="table-container"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0.5rem",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          padding: "1rem",
          marginTop: "1.5rem",
        }}
      >
        <Table
          className="custom-table"
          columns={columns(openPdf)}
          dataSource={paginatedData}
          pagination={false}
        />
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredData.length}
          onChange={handlePageChange}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          onShowSizeChange={handlePageSizeChange}
          style={{ marginTop: "20px", textAlign: "right" }}
        />
      </div>

      {/* Action Buttons Section */}
      <CSVLink
        data={filteredData}
        headers={csvHeaders}
        filename={`industrial-visit_${new Date().toISOString()}.csv`}
      >
        <Button type="primary" className="exportcsv">
          <ExportOutlined />
          Export to CSV
        </Button>
      </CSVLink>
    </div>
  );
};

export default MouStatus;
